<script lang="ts">
 /**
 * Root orchestrator for the capture experience.
 *
 * @remarks
 * Composes all capture sub-components (viewfinder, controls, drawers,
 * overlays) into a full-screen camera-style layout. Owns the
 * {@link useCaptureFlow} state machine and wires events between children.
 *
 * **Layout structure** (top to bottom):
 * 1. **CaptureContextBar** — current container + back button
 * 2. **Viewfinder region** — camera feed with scan overlay, floating tray + controls
 * 3. **CaptureActionBar** — Item/Container mode picker
 * 4. **Drawer overlays** — photo strip, processing, suggestion, container prompt, picker
 *
 * **Drawer close handling**: Every drawer component emits `update:open`.
 * The shell guards each handler so that programmatic state transitions
 * (e.g. processing → suggestion) don't accidentally reset the flow when
 * the processing drawer closes.
 *
 * @example
 * ```vue
 * <CaptureShell
 *   :container-name="containerName"
 *   :container-ancestors="ancestors"
 *   :recent-containers="recent"
 *   :all-containers="all"
 * />
 * ```
 */
</script>

<script setup lang="ts">
import { router } from "@inertiajs/vue3"
import { onUnmounted, ref, shallowRef, useTemplateRef, watch } from "vue"

import ContainerPicker from "@/components/ContainerPicker.vue"
import { useBarcodeScanner } from "@/composables/useBarcodeScanner"
import { useCaptureFlow } from "@/composables/useCaptureFlow"
import { useSpeechRecognition } from "@/composables/useSpeechRecognition"
import { getJson, patchJson } from "@/lib/fetch"
import { ICON } from "@/lib/icons"
import { captureBarcodePath, capturePath, convertCaptureContainerPath, itemPath } from "@/routes"
import type { BreadcrumbSegment, ContainerSummary } from "@/types/models"

import BulkCaptureShell from "./BulkCaptureShell.vue"
import CaptureActionBar from "./CaptureActionBar.vue"
import CaptureContainerPrompt from "./CaptureContainerPrompt.vue"
import CaptureContextBar from "./CaptureContextBar.vue"
import CaptureControls from "./CaptureControls.vue"
import CapturePhotoStrip from "./CapturePhotoStrip.vue"
import CapturePhotoTray from "./CapturePhotoTray.vue"
import CaptureProcessingCard from "./CaptureProcessingCard.vue"
import CaptureScanOverlay from "./CaptureScanOverlay.vue"
import CaptureSuggestionCard from "./CaptureSuggestionCard.vue"
import CaptureViewfinder from "./CaptureViewfinder.vue"

const props = defineProps<{
  /** Trusted ID of the active account from Inertia shared props. */
  accountId: string
  /** UUID of the active container, or null if none selected. */
  containerId: string | null
  /** Display name of the active container, or null if none selected. */
  containerName: string | null
  /** Breadcrumb path from root to current container (for context bar). */
  containerAncestors: BreadcrumbSegment[]
  /** Recently used containers for quick access in the picker. */
  recentContainers: ContainerSummary[]
  /** All containers in the account, for searching in the picker. */
  allContainers: ContainerSummary[]
  /** Pre-captured images from Web Share Target API (consumed once on mount). */
  sharedImages: string[] | null
}>()

/** Explicit mode switch. Normal Capture remains the default entry flow. */
const bulkMode = shallowRef(false)
const nativePreview = shallowRef(false)
const cameraBusy = shallowRef(false)

/** Template ref for the shared live scanner and native-camera handoff. */
const viewfinderRef = useTemplateRef<InstanceType<typeof CaptureViewfinder>>("viewfinderRef")

/** The capture flow state machine — all UI visibility and transitions stem from this. */
const flow = useCaptureFlow()

/** Sync container ID from props into the flow's reactive state. */
watch(
  () => props.containerId,
  (id) => {
    flow.containerId.value = id
  },
  { immediate: true },
)

/** Pre-populate photos from shared images (Web Share Target API). */
if (props.sharedImages?.length) {
  for (const img of props.sharedImages) flow.capture(img)
}

/** Speech recognition for voice hints. Transcript syncs to flow.hint. */
const speech = useSpeechRecognition()

watch(speech.transcript, (text) => {
  if (speech.isListening.value) flow.hint.value = text
})

/** Sync flow state if speech recognition ends unexpectedly (e.g. silence timeout). */
watch(speech.isListening, (listening) => {
  if (!listening && flow.isVoicePreHint.value) flow.stopVoicePreHint()
})

/** Toggle voice recording for the hint field. */
function toggleVoice() {
  if (speech.isListening.value) {
    speech.stop()
    if (flow.isVoicePreHint.value) flow.stopVoicePreHint()
  } else {
    speech.start()
    if (flow.isIdle.value) flow.startVoicePreHint()
  }
}

/** Barcode scanner — detects QR/barcodes from the camera stream. */
const scanner = useBarcodeScanner(handleBarcodeDetected)

/** Stop barcode processing when scan mode closes. */
watch(
  () => flow.scanning.value,
  (active) => {
    if (!active) scanner.stop()
  },
)

/** State for the "existing item found" drawer. */
const foundItem = ref<{
  id: string
  name: string
  code: string
  archived: boolean
  isContainer: boolean
} | null>(null)
const foundItemDrawerOpen = ref(false)
const convertingItem = ref(false)

/**
 * Look up a detected barcode via the backend.
 *
 * Keeps the scan overlay visible with a "Looking up..." status while
 * the request is in flight, then dismisses scanning in each outcome.
 */
async function handleBarcodeDetected(code: string) {
  if (bulkMode.value) return
  flow.scanStatus.value = `Looking up ${code}…`

  try {
    const response = await getJson(`${captureBarcodePath()}?barcode=${encodeURIComponent(code)}`)
    if (bulkMode.value) return

    if (response.ok) {
      const data = await response.json()
      if (bulkMode.value) return
      flow.stopScanning()
      if (data.is_container && !data.archived_at) {
        router.visit(capturePath({ container_id: data.id }), { preserveState: false })
      } else {
        foundItem.value = {
          id: data.id,
          name: data.name,
          code,
          archived: !!data.archived_at,
          isContainer: data.is_container,
        }
        foundItemDrawerOpen.value = true
      }
    } else if (response.status === 404) {
      flow.pendingAssetCode.value = code
      flow.scanStatus.value = `✓ Code captured: ${code}`
      setTimeout(() => {
        if (!bulkMode.value) flow.stopScanning()
      }, 1500)
      return
    } else {
      flow.stopScanning()
    }
  } catch {
    if (!bulkMode.value) flow.stopScanning()
  }
}

/** Convert an existing item to a container and switch capture context to it. */
async function handleConvertToContainer() {
  if (!foundItem.value) return
  convertingItem.value = true

  try {
    const response = await patchJson(convertCaptureContainerPath(), {
      item_id: foundItem.value.id,
    })

    if (response.ok) {
      const data = await response.json()
      foundItemDrawerOpen.value = false
      foundItem.value = null
      router.visit(capturePath({ container_id: data.id }), { preserveState: false })
    }
  } catch {
    // Network error — drawer stays open, user can retry
  } finally {
    convertingItem.value = false
  }
}

/** Dismiss the found-item drawer without taking action. */
function dismissFoundItem() {
  foundItemDrawerOpen.value = false
  foundItem.value = null
}

/** Navigate to the item show page for the found item. */
function handleViewFoundItem() {
  if (!foundItem.value) return
  const id = foundItem.value.id
  foundItemDrawerOpen.value = false
  foundItem.value = null
  router.visit(itemPath(id))
}

/** Abort in-flight requests and clean up when the component unmounts. */
onUnmounted(() => flow.reset())

/**
 * Handle shutter press — synchronously hand off to Android's native camera.
 */
function handleCapture(): void {
  if (speech.isListening.value) speech.stop()
  viewfinderRef.value?.openNativeCamera()
}

/** Add the native camera's returned original file to the normal capture flow. */
async function handlePhotosSelected(photos: File[]): Promise<void> {
  const photo = photos[0]
  if (!photo) return
  flow.capture(await blobToDataUrl(photo))
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener("load", () => resolve(reader.result as string), { once: true })
    reader.addEventListener(
      "error",
      () => reject(reader.error ?? new Error("Could not read captured photo")),
      { once: true },
    )
    reader.readAsDataURL(blob)
  })
}

/**
 * Handle container selection from the picker drawer.
 *
 * Reloads the capture page with the new container ID, which updates
 * the session and refreshes all container-related props via Inertia.
 */
function handleContainerSelect(container: ContainerSummary) {
  flow.pickerOpen.value = false
  router.visit(capturePath({ container_id: container.id }), { preserveState: false })
}

/** Clear the active capture container and the server-side session context. */
function handleContainerClear() {
  flow.pickerOpen.value = false
  router.visit(capturePath({ container_id: "" }), { preserveState: false })
}

/**
 * Set the newly created container as the active capture context.
 *
 * Reloads the capture page with the new container ID so subsequent
 * items are cataloged into it.
 */
function handleSetContainer() {
  const newId = flow.lastCreated.value?.id
  flow.setContainerAndReset()
  if (newId) {
    router.visit(capturePath({ container_id: newId }), { preserveState: false })
  }
}

/**
 * Handle back button press — exit capture mode.
 */
function handleBack() {
  router.visit("/")
}

function enterBulkCapture() {
  if (speech.isListening.value) speech.stop()
  scanner.stop()
  flow.stopScanning()
  bulkMode.value = true
}
</script>

<template>
  <BulkCaptureShell
    v-if="bulkMode"
    :account-id="accountId"
    :container-id="containerId"
    :container-name="containerName"
    :container-ancestors="containerAncestors"
    :recent-containers="recentContainers"
    :all-containers="allContainers"
    @exit="bulkMode = false"
  />
  <div
    v-else
    class="fixed inset-0 flex w-full flex-col overflow-hidden"
    :class="nativePreview ? 'bg-transparent' : 'bg-black'"
  >
    <!-- Context bar — always visible -->
    <CaptureContextBar
      :ancestors="containerAncestors"
      :container-name="containerName"
      @open-picker="flow.togglePicker()"
      @back="handleBack"
    />

    <!-- Viewfinder + floating controls — relative wrapper for positioning -->
    <div class="relative flex-1">
      <CaptureViewfinder
        ref="viewfinderRef"
        :frozen="flow.isProcessing.value || flow.isSuggestion.value"
        :scanner-active="flow.scanning.value"
        zoom-position="above-capture-controls"
        @photos-selected="handlePhotosSelected"
        @native-preview="nativePreview = $event"
        @update:busy="cameraBusy = $event"
        @scanner-ready="scanner.start"
      >
        <CaptureScanOverlay :active="flow.scanning.value" :status="flow.scanStatus.value" />
      </CaptureViewfinder>

      <!-- Success pill — auto-dismisses after 2s -->
      <Transition
        enter-from-class="translate-y-4 opacity-0"
        enter-active-class="transition duration-300"
        leave-active-class="transition duration-300"
        leave-to-class="-translate-y-2 opacity-0"
      >
        <div
          v-if="flow.successPill.value"
          class="absolute inset-x-0 top-4 z-20 flex justify-center"
        >
          <div
            class="flex items-center gap-2 rounded-full bg-black/70 py-1.5 pr-4 pl-1.5 shadow-lg backdrop-blur-sm"
          >
            <img
              v-if="flow.successPill.value.thumbUrl"
              :src="flow.successPill.value.thumbUrl"
              class="size-7 rounded-full object-cover"
              alt=""
            />
            <UIcon v-else name="i-lucide-check-circle" class="size-5 text-green-400" />
            <span class="text-sm font-medium text-white">
              {{ flow.successPill.value.name }} added
            </span>
          </div>
        </div>
      </Transition>

      <!-- Floating controls — single container, no z-index conflicts -->
      <div
        v-if="flow.showBottomUI.value && !flow.pickerOpen.value"
        class="absolute inset-x-0 bottom-8 flex flex-col items-center gap-4"
      >
        <UButton
          data-testid="start-bulk-capture"
          color="neutral"
          variant="solid"
          :icon="ICON.bulkCapture"
          class="rounded-full bg-black/70 text-white backdrop-blur-sm"
          @click="enterBulkCapture"
        >
          Start bulk capture
        </UButton>

        <!-- Pending asset code badge — visible on main screen when a code is queued -->
        <div
          v-if="flow.pendingAssetCode.value"
          class="flex items-center gap-2 rounded-full bg-black/70 py-1.5 pr-2 pl-3 backdrop-blur-sm"
        >
          <UIcon name="i-lucide-qr-code" class="size-3.5 text-white/70" />
          <span class="text-xs font-medium text-white/90">{{ flow.pendingAssetCode.value }}</span>
          <button
            aria-label="Remove code"
            class="flex size-5 items-center justify-center rounded-full text-white/50 hover:text-white"
            @click="flow.pendingAssetCode.value = null"
          >
            <UIcon name="i-lucide-x" class="size-3" />
          </button>
        </div>

        <CapturePhotoTray
          v-if="flow.isCapturing.value"
          :photos="flow.photos.value"
          @review="flow.review()"
          @analyze="flow.analyze()"
          @clear="flow.clear()"
        />
        <CaptureControls
          :voice-active="speech.isListening.value"
          :has-photos="flow.photos.value.length > 0"
          :capture-busy="cameraBusy"
          @capture="handleCapture"
          @analyze="flow.analyze()"
          @toggle-mic="toggleVoice"
          @toggle-scan="flow.toggleScanning()"
        />
      </div>
    </div>

    <!-- Bottom bar — just the mode picker -->
    <CaptureActionBar
      v-if="flow.showBottomUI.value && !flow.pickerOpen.value"
      :mode="flow.mode.value"
      @update:mode="flow.mode.value = $event"
    />

    <!-- Review drawer — full photo strip with hint + analyze/clear -->
    <CapturePhotoStrip
      :open="flow.isPhotoStrip.value"
      :photos="flow.photos.value"
      :hint="flow.hint.value"
      :voice-active="speech.isListening.value"
      :pending-asset-code="flow.pendingAssetCode.value"
      @update:open="!$event && flow.clear()"
      @add-photo="handleCapture"
      @remove-photo="flow.removePhoto($event)"
      @update:hint="flow.hint.value = $event"
      @analyze="flow.analyze()"
      @clear="flow.clear()"
      @toggle-voice="toggleVoice"
      @clear-asset-code="flow.pendingAssetCode.value = null"
    />

    <CaptureProcessingCard
      :open="flow.isProcessing.value"
      @update:open="!$event && flow.isProcessing.value && flow.reset()"
    />

    <!-- Error drawer — shown when AI analysis fails -->
    <UDrawer :open="flow.isError.value" @update:open="!$event && flow.retryAnalysis()">
      <template #content>
        <div class="flex flex-col items-center gap-4 p-6">
          <UIcon name="i-lucide-alert-triangle" class="text-error size-8" />
          <p class="text-muted text-center text-sm">
            {{ flow.error.value ?? "Analysis failed" }}
          </p>
          <div class="flex w-full flex-col gap-2">
            <UButton block size="xl" @click="flow.analyze()">Try again</UButton>
            <UButton block size="xl" variant="ghost" color="neutral" @click="flow.retryAnalysis()">
              Back to photos
            </UButton>
          </div>
        </div>
      </template>
    </UDrawer>

    <!-- Existing item found drawer — convert to container, view, or cancel -->
    <UDrawer :open="foundItemDrawerOpen" @update:open="!$event && dismissFoundItem()">
      <template #content>
        <div class="flex flex-col items-center gap-4 p-6">
          <UIcon name="i-lucide-package-search" class="text-primary size-8" />
          <p class="text-center text-sm font-medium">"{{ foundItem?.name }}" already exists</p>
          <UAlert
            v-if="foundItem?.archived"
            color="warning"
            title="Archived item"
            description="Restore this item before using it in capture."
          />
          <div class="flex w-full flex-col gap-2">
            <UButton
              v-if="!foundItem?.archived"
              block
              size="xl"
              :loading="convertingItem"
              icon="i-lucide-box"
              @click="handleConvertToContainer"
            >
              Convert to container
            </UButton>
            <UButton
              block
              size="xl"
              variant="outline"
              icon="i-lucide-eye"
              @click="handleViewFoundItem"
            >
              View item
            </UButton>
            <UButton block size="xl" variant="ghost" color="neutral" @click="dismissFoundItem">
              Cancel
            </UButton>
          </div>
        </div>
      </template>
    </UDrawer>

    <CaptureSuggestionCard
      :open="flow.isSuggestion.value && !!flow.suggestion.value"
      :name="flow.suggestion.value?.name ?? ''"
      :description="flow.suggestion.value?.description ?? null"
      :quantity="flow.suggestion.value?.quantity ?? 1"
      :item-type="flow.suggestion.value?.item_type_name ?? null"
      :container-name="containerName"
      :photos="flow.photos.value"
      :saving="flow.saving.value"
      @update:open="!$event && flow.isSuggestion.value && flow.dismiss()"
      @confirm="flow.confirm()"
      @refine="flow.refine($event)"
      @edit="() => {}"
    />

    <CaptureContainerPrompt
      :open="flow.isContainerPrompt.value"
      :container-name="flow.lastCreated.value?.name ?? 'Container'"
      @update:open="!$event && flow.reset()"
      @set-container="handleSetContainer"
      @done="flow.reset()"
    />

    <!-- Container picker overlay -->
    <ContainerPicker
      :open="flow.pickerOpen.value"
      :recent-containers="recentContainers"
      :all-containers="allContainers"
      :allow-clear="true"
      @update:open="flow.pickerOpen.value = $event"
      @select="handleContainerSelect"
      @clear="handleContainerClear"
    />
  </div>
</template>
